// frame: wLOFS @ ui/dogdisk-agent.pen (弹框-修改密码 440×440)
// spec: docs/ui/agent-page-flows.md §yDAqU row1 + §pen-to-code-backlog.md §3.4 B5 wLOFS
//
// 复用 POST /gapi/auth/change-password(已在 backend 落地)。
// 与 profile.tsx 内置的 PasswordModal 等价 — 此处独立成 modal 模块,便于其它入口(settings.tsx /
// avatar dropdown / 直接 deeplink)调用。
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Modal, useToast } from '@dogdisk/shared'
import { changePassword } from '../_lib/agent-api'

const schema = z
  .object({
    old_password: z.string().min(8, '当前密码至少 8 位'),
    new_password: z.string().min(8, '新密码至少 8 位'),
    new_password_confirm: z.string().min(8),
  })
  .refine((d) => d.new_password === d.new_password_confirm, {
    path: ['new_password_confirm'],
    message: '两次输入不一致',
  })
type Form = z.infer<typeof schema>

export function ChangePasswordModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Form>()

  const onSubmit = async (vals: Form) => {
    setBusy(true)
    setError(null)
    try {
      const parsed = schema.parse(vals)
      await changePassword({
        old_password: parsed.old_password,
        new_password: parsed.new_password,
      })
      toast.push('密码已更新,请重新登录', { variant: 'success' })
      reset()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : '修改失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="修改密码">
      <form
        data-testid="wLOFS"
        data-frame-id="wLOFS"
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-3"
      >
        <Field
          label="当前密码"
          error={errors.old_password?.message}
          input={
            <input
              type="password"
              autoComplete="current-password"
              {...register('old_password')}
              className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
              data-testid="cp-old"
            />
          }
        />
        <Field
          label="新密码(至少 8 位)"
          error={errors.new_password?.message}
          input={
            <input
              type="password"
              autoComplete="new-password"
              {...register('new_password')}
              className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
              data-testid="cp-new"
            />
          }
        />
        <Field
          label="确认新密码"
          error={errors.new_password_confirm?.message}
          input={
            <input
              type="password"
              autoComplete="new-password"
              {...register('new_password_confirm')}
              className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
              data-testid="cp-confirm"
            />
          }
        />
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="h-10 rounded-md bg-amber-500 text-sm font-semibold text-white disabled:opacity-60"
          data-testid="cp-submit"
        >
          {busy ? '提交中…' : '确认修改'}
        </button>
      </form>
    </Modal>
  )
}

function Field({
  label,
  input,
  error,
}: {
  label: string
  input: React.ReactNode
  error?: string | undefined
}) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="font-medium text-gray-700">{label}</span>
      {input}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </label>
  )
}
